/**
 * Side-by-side estimates (roadmap 1C, V-8).
 *
 * When several people estimate the same item and criterion, show each
 * person's estimate next to the others and say plainly whether they are far
 * apart — "Finance 8, Engineering 3 — far apart".
 *
 * "Far apart" uses the flag stored when estimates are combined
 * (accepted_values.is_contested, set by valueProposalStore.aggregateEstimates)
 * when there is one, and otherwise the same rule that set it: a standard
 * deviation above 1.0 (CONTESTED_STD_DEV). Values that are not numbers are far
 * apart when they are not all the same.
 */

import { stdDev } from './noiseAuditService';

/** The rule valueProposalStore uses to mark combined estimates as contested. */
export const CONTESTED_STD_DEV = 1.0;

export interface EstimateEntry {
  who: string;
  value: unknown;
}

export interface EstimateSpread {
  /** "Finance 8, Engineering 3" */
  sideBySide: string;
  farApart: boolean;
  /** "far apart" | "close" | "the same" */
  verdict: string;
  /** "Finance 8, Engineering 3 — far apart" */
  line: string;
}

function estimateText(v: unknown): string {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
  return String(v ?? '—');
}

/**
 * Null when fewer than two estimates — there is nothing to compare.
 * `storedContested` is the flag saved with the combined value, when there is one.
 */
export function describeEstimateSpread(
  entries: readonly EstimateEntry[],
  storedContested?: boolean | null,
): EstimateSpread | null {
  if (entries.length < 2) return null;
  const sideBySide = entries.map((e) => `${e.who} ${estimateText(e.value)}`).join(', ');

  const nums = entries.map((e) => Number(e.value));
  const allNumeric = nums.every((n) => !Number.isNaN(n)) && entries.every((e) => e.value !== '' && e.value != null);
  const allSame = new Set(entries.map((e) => (allNumeric ? Number(e.value) : String(e.value)))).size === 1;

  let farApart: boolean;
  if (typeof storedContested === 'boolean') farApart = storedContested;
  else if (allNumeric) farApart = stdDev(nums) > CONTESTED_STD_DEV;
  else farApart = !allSame;

  const verdict = farApart ? 'far apart' : allSame ? 'the same' : 'close';
  return { sideBySide, farApart, verdict, line: `${sideBySide} — ${verdict}` };
}
